import { ld } from "@/lib/theme/light-dark";
import { Bell, KeyRound, Mail, Phone, UserPlus, Users, X } from "lucide-react";
import type { ReactNode } from "react";

import { useTranslations } from "next-intl";
import { H } from "../../hero-tokens";
import { HERO_VORGANG_RAIL_WIDTH_PX } from "./hero-vorgang-tokens";

export type HeroUnknownContactData = {
  readonly kind: "email" | "phone" | "portal";
  readonly displayName?: string;
  readonly identifier: string;
  readonly remainingCount: number;
};

export function HeroVorgangUnknownPanel(props: {
  readonly contact: HeroUnknownContactData;
}): ReactNode {
  const t = useTranslations("marketing.heroVorgangUnknownPanel");
  const contact: HeroUnknownContactData = props.contact;

  return (
    <div
      style={{
        width: HERO_VORGANG_RAIL_WIDTH_PX,
        flexShrink: 0,
        borderLeft: `1px solid ${ld("#EEF0F3", "#2c2f33")}`,
        background: ld("#fff", "#181b1f"),
        position: "relative",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "36px 16px 24px",
        boxSizing: "border-box",
        overflowY: "auto",
        textAlign: "center",
      }}
    >
      <span
        style={{
          position: "absolute",
          top: 10,
          right: 10,
          width: 28,
          height: 28,
          borderRadius: "50%",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          color: ld("rgba(11,15,25,0.40)", "rgba(250,250,250,0.4)"),
          cursor: "default",
        }}
      >
        <X width={16} height={16} strokeWidth={2} />
      </span>

      {contact.remainingCount >= 1 ? (
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "4px 10px",
            borderRadius: 999,
            border: `1px solid ${ld("rgba(11,15,25,0.10)", "rgba(250,250,250,0.1)")}`,
            background: "rgba(58,158,107,0.04)",
            fontSize: 11,
            fontWeight: 500,
            color: ld("rgba(11,15,25,0.60)", "rgba(250,250,250,0.6)"),
            marginBottom: 16,
          }}
        >
          <Users width={12} height={12} strokeWidth={1.9} />
          {t("neueKontakteGefunden", { count: contact.remainingCount })}
        </span>
      ) : null}

      <span
        style={{
          width: 56,
          height: 56,
          borderRadius: "50%",
          border: `1px solid ${ld("rgba(11,15,25,0.12)", "rgba(250,250,250,0.12)")}`,
          background: "rgba(58,158,107,0.03)",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          color: ld("rgba(11,15,25,0.40)", "rgba(250,250,250,0.4)"),
          marginBottom: 14,
        }}
      >
        {contact.kind === "phone" ? (
          <Phone width={24} height={24} strokeWidth={1.8} />
        ) : contact.kind === "portal" ? (
          <KeyRound width={24} height={24} strokeWidth={1.8} />
        ) : (
          <Mail width={24} height={24} strokeWidth={1.8} />
        )}
      </span>

      <p
        style={{
          margin: "0 0 6px",
          fontSize: 15,
          fontWeight: 600,
          color: ld("rgba(11,15,25,0.90)", "rgba(250,250,250,0.9)"),
        }}
      >
        {t("unbekannterKontakt")}
      </p>

      <p
        style={{
          margin: "0 0 8px",
          fontSize: 13,
          lineHeight: 1.45,
          color: ld("rgba(11,15,25,0.60)", "rgba(250,250,250,0.6)"),
          wordBreak: "break-word",
        }}
      >
        {contact.displayName !== undefined && contact.kind === "email" ? (
          <>
            <span style={{ fontWeight: 500 }}>{contact.displayName}</span>{" "}
            <span style={{ wordBreak: "break-all" }}>
              {t("lt")}
              {contact.identifier}
              {t("gt")}
            </span>
          </>
        ) : (
          <span style={{ fontWeight: 500 }}>{contact.identifier}</span>
        )}
      </p>

      <p
        style={{
          margin: "0 0 18px",
          fontSize: 12,
          lineHeight: 1.4,
          color: ld("rgba(11,15,25,0.40)", "rgba(250,250,250,0.4)"),
        }}
      >
        {contact.kind === "portal"
          ? t("ordneDiesenPortalKontakt")
          : contact.kind === "phone"
            ? t("ordneDieseNummerKontakt")
            : t("ordneDieseAdresseKontakt")}
      </p>

      <div
        style={{
          width: "100%",
          maxWidth: 240,
          display: "flex",
          flexDirection: "column",
          gap: 8,
        }}
      >
        <span
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            height: 36,
            borderRadius: 8,
            background: H.accent,
            color: ld("#fff", "#181b1f"),
            fontSize: 13,
            fontWeight: 500,
            cursor: "default",
          }}
        >
          <UserPlus width={14} height={14} strokeWidth={1.9} />
          {t("kontaktAnlegen")}
        </span>
        <span
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            height: 36,
            borderRadius: 8,
            border: `1px solid ${ld("#E7E9ED", "#32363a")}`,
            background: ld("#fff", "#181b1f"),
            fontSize: 13,
            fontWeight: 500,
            color: ld("#0B0F19", "#fafafa"),
            cursor: "default",
          }}
        >
          <Users width={14} height={14} strokeWidth={1.9} />
          {t("bestehendemKontaktZuordnen")}
        </span>
        <span
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            height: 32,
            fontSize: 11,
            color: ld("rgba(11,15,25,0.50)", "rgba(250,250,250,0.5)"),
            cursor: "default",
          }}
        >
          <Bell width={14} height={14} strokeWidth={1.9} />
          {t("benachrichtigungMarkieren")}
        </span>
      </div>
    </div>
  );
}
